import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, AppBar, Toolbar, IconButton, Tooltip, Modal, Typography, Paper, Divider, Grid } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import WatchLaterIcon from '@mui/icons-material/WatchLater';
import CloseIcon from '@mui/icons-material/Close';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css'; // Make sure you have this import in your project
import { useUser } from '../../services/userContext';

const TBondLayout = ({ children }) => {
  const { user } = useUser();
  const [localTimeLeft, setLocalTimeLeft] = useState(0);
  const [hourToastShown, setHourToastShown] = useState(false);
  const [halfHourToastShown, setHalfHourToastShown] = useState(false);
  const [tenMinToastShown, setTenMinToastShown] = useState(false);
  const [clockColor, setClockColor] = useState('#007A7A'); // Initial color
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const calculateTimeLeft = () => {
      const expiryTime = localStorage.getItem('sessionExpiry');
      if (expiryTime) {
        const currentTime = Date.now();
        return Math.max(expiryTime - currentTime, 0);
      }
      return 0;
    };

    const checkTimeIntervals = (timeLeft) => {
      const oneHourInMs = 60 * 60 * 1000;
      const halfHourInMs = 30 * 60 * 1000;
      const tenMinutesInMs = 10 * 60 * 1000;

      if (timeLeft <= oneHourInMs && !hourToastShown) {
        toast.info('You have 1 hour left in your session. Click the Clock To Refresh.', {
          autoClose: false, // Disable autoclear
        });
        setClockColor('#F18F01'); // Change color to orange at 1 hour
        setHourToastShown(true);
      }

      if (timeLeft <= halfHourInMs && !halfHourToastShown) {
        toast.warning('You have 30 minutes left in your session. Click the Clock To Refresh.', {
          autoClose: false, // Disable autoclear
        });
        setClockColor('yellow'); // Change color to yellow at 30 minutes
        setHalfHourToastShown(true);
      }

      if (timeLeft <= tenMinutesInMs && !tenMinToastShown) {
        toast.error('You have 10 minutes left in your session. Click the Clock To Refresh.', {
          autoClose: false, // Disable autoclear
        });
        setClockColor('red'); // Change color to red at 10 minutes
        setTenMinToastShown(true);
      }
    };

    const updateTimer = () => {
      const timeLeft = calculateTimeLeft();
      setLocalTimeLeft(timeLeft);

      checkTimeIntervals(timeLeft);

      if (timeLeft <= 0) {
        navigate('/session-expired');
      }
    };

    updateTimer();

    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [navigate, hourToastShown, halfHourToastShown, tenMinToastShown]);

  const formatTime = (milliseconds) => {
    if (milliseconds <= 0) return '0:00:00';
    const totalSeconds = Math.floor(milliseconds / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleRefreshSession = () => {
    user?.refreshSession();
    setClockColor('#007A7A');
  }

  const handleOpenPhoneModal = () => setPhoneModalOpen(true);
  const handleClosePhoneModal = () => setPhoneModalOpen(false);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <AppBar position="static">
        <Toolbar sx={{ justifyContent: 'space-between', minHeight: 56, px: 2, position: 'relative' }}>
          {/* Logo in Top Left Corner */}
          <Box sx={{ position: 'absolute', top: '15px', left: '20px', display: 'flex', alignItems: 'center' }}>
            <Box
              component="img"
              src="./bankers-surety-logo.png"
              alt="Logo"
              sx={{
                height: 30,
              }}
            />
          </Box>

          {/* Centered TransferPro Web Portal */}
          <Box
            sx={{
              position: 'absolute',
              left: '50%',
              top: '50%',
              transform: 'translate(-50%, -50%)',
              display: 'flex',
              alignItems: 'center', // Align vertically
              gap: 1, // Space between the texts
            }}
          >
            <Typography
              variant="h6"
              sx={{
                color: '#007A7A',
                fontWeight: 'bold',
                fontSize: '1.55rem',
              }}
            >
              TransferPRO
            </Typography>
            <Typography
              variant="h6"
              sx={{
                color: '#F18F01',
                fontWeight: 'lighter' ,
                fontSize: '1.55rem',
              }}
            >
              Web Portal
            </Typography>
          </Box>

          {/* Clock Icon and Phone Icon on the Right */}
          <Box sx={{ display: 'flex', alignItems: 'center', ml: 'auto' }}>
            <Tooltip title={`Time Remaining: ${formatTime(localTimeLeft)} Click to Refresh`}>
              <IconButton
                sx={{
                  color: clockColor,
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.2)',
                  },
                }}
                onClick={handleRefreshSession}
                aria-label="refresh session"
              >
                <WatchLaterIcon fontSize="large" />
              </IconButton>
            </Tooltip>

            <Tooltip title="Contact Information">
              <IconButton
                sx={{
                  color: 'primary.main', // Primary color
                  cursor: 'pointer',
                  transition: 'transform 0.2s ease-in-out',
                  '&:hover': {
                    transform: 'scale(1.2)',
                  },
                  ml: 2, // Margin to separate the icons
                }}
                onClick={handleOpenPhoneModal}
                aria-label="contact information"
              >
                <PhoneIcon fontSize="large" />
              </IconButton>
            </Tooltip>
          </Box>
        </Toolbar>
      </AppBar>

      {/* Phone Modal */}
      <Modal open={phoneModalOpen} onClose={handleClosePhoneModal}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: '90%',
          maxWidth: 600,
          bgcolor: 'background.paper',
          boxShadow: 24,
          borderRadius: 8,
          p: 4,
          maxHeight: '80vh',
          overflowY: 'auto',
        }}
      >
        {/* Modal Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography id="contact-info-title" variant="h6" component="h2">
            Contact Information
          </Typography>
          <IconButton onClick={handleClosePhoneModal} aria-label="close">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Contact Numbers */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            TransferBond Team Phone Number
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2">
            <strong>Toll Free:</strong> 877-778-5245
          </Typography>
        </Box>

        {/* Emails */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
            TransferBond Team Email
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="body2">
            <strong>Email:</strong> transferbonds@bankersinsurance.com
          </Typography>
        </Box>
      </Box>

      </Modal>

      <Box sx={{ flex: 1, overflowY: 'auto' }}>
        {children}
      </Box>
    </Box>
  );
};

export default TBondLayout;
